import { Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Stack, Typography, Box } from "@mui/material";
import { useGetTagsForSearchTermQuery, useGetTagsListQuery, useTagSearchTermMutation, useUntagSearchTermMutation } from "src/context/api/tags/api";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import { useEffect, useMemo, useRef, useState } from "react";
import Iconify from "src/components/iconify";
import GenericInfiniteScrollList from "src/sections/overview/dashboards/views/generic-infinite-scroll-list";
import { enqueueSnackbar } from "notistack";
import { useForm } from "react-hook-form";
import FormProvider from "src/components/hook-form";
import { useBoolean } from "src/hooks/use-boolean";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { ClusterChip, chipKey, chipRegionCode, isChipDeletable } from "../chips";
import { ClusterModule } from "../config";
import { CountryItem } from "src/components/country-bar";
import CustomTooltip from "src/components/visibility/components/CustomTooltip";

const schema = yup.object().shape({
    search_term: yup.string().required(),
    tags: yup.array().of(yup.string().required()).required().min(1),
});

type TagsCellProps = {
    tags: ClusterChip[];
    row: { id: string };
    module: ClusterModule;
    region: string;
    regionScoped: boolean;
    countries: CountryItem[];
};

export default function TagsCell({ tags, row, module, region, regionScoped, countries }: TagsCellProps) {
    const workspace = useCurrentWorkspace();
    const [anchorEl,] = useState<HTMLElement | null>(null);
    const [untagSearchTerm] = useUntagSearchTermMutation();
    const [tagSearchTerm] = useTagSearchTermMutation();

    const { data: { tags: tagsList = [] } = {} } = useGetTagsListQuery({ workspaceId: workspace?.id as number, module }, { skip: !workspace?.id });
    const { data: { tags: tagsForSearchTerm = [] } = {} } = useGetTagsForSearchTermQuery({ workspaceId: workspace?.id as number, searchTerm: row.id, module, region }, { skip: !workspace?.id || !row.id });
    const addTagsModal = useBoolean(false);
    const defaultValues = {
        search_term: row.id,
        tags: [] as string[],
    };


    const methods = useForm({
        resolver: yupResolver(schema),
        defaultValues,
    });

    const [tagsOptions, setTagsOptions] = useState<any[]>([]);
    const hasCurrentTags = useRef(false);

    useEffect(() => {
        // Guard against looping when the query has no result yet: `{ data: { tags: tagsList = [] } = {} }`
        // creates a brand new `[]` on every render while `data` is undefined (loading, skipped, or
        // errored query, e.g. a 403), so the `[tagsList]` effect below would re-run and re-set state
        // on every render instead of just once.
        const newTags = (tagsList?.filter((tag: any) => !tagsOptions.some((t: any) => t.label === tag.label)).map((tag: any) => ({
            id: tag.id,
            label: tag.name,
        }))) || [];
        if (newTags.length === 0) return;
        setTagsOptions((prev) => [...prev, ...newTags]);
    }, [tagsList]);

    useEffect(() => {
        if (tagsForSearchTerm?.length !== methods.getValues("tags").length) {
            methods.setValue("tags", tagsForSearchTerm?.map((tag: any) => tag.name) || [] as string[]);
        }
        if (hasCurrentTags.current) return;
        if (tagsForSearchTerm?.length > 0) {
            hasCurrentTags.current = true;
            setTagsOptions((prev) => [...prev, ...(tagsForSearchTerm?.map((tag: any) => ({
                id: tag.id,
                label: tag.name,
            }))) || []]);
            methods.setValue("tags", tagsForSearchTerm?.map((tag: any) => tag.name) || [] as string[]);
        }
    }, [tagsForSearchTerm]);

    const deduplicateTagsOptions = useMemo(() => {
        return Array.from(new Set(tagsOptions.map((tag: any) => tag.label))).map((label: string) => tagsOptions.find((tag: any) => tag.label === label));
    }, [tagsOptions]);

    const onSubmit = (data: any) => {
        // Send only the clusters the user added. The dialog is pre-filled with the term's
        // current clusters; re-sending them from the All tab would turn its regional
        // assignments into global ones (a global assignment absorbs the regional ones).
        const assigned = new Set(tagsForSearchTerm.map((tag: any) => tag.name));
        const added: string[] = data.tags.filter((tag: string) => !assigned.has(tag));
        if (added.length === 0) {
            addTagsModal.onFalse();
            return;
        }
        tagSearchTerm({ workspaceId: workspace?.id as number, searchTerm: row.id, tags: added.map((tag) => ({ label: tag })), module, region })
            .unwrap()
            .then(() => {
                addTagsModal.onFalse();
                methods.reset();
                enqueueSnackbar("Searchterm clustered successfully", { variant: "success" });
            })
            .catch((error) => {
                enqueueSnackbar("Searchterm clustering failed", { variant: "error" });
            });
    }

    return (
        <Stack direction="row" justifyContent="space-between" height="100%" gap={0} sx={{ overflow: 'hidden', width: '100%', px: 0 }}>
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    scrollbarWidth: 'none',

                    '&::-webkit-scrollbar': {
                        display: 'none',
                    },
                }}
            >
                <Stack width="fit-content" direction="row" alignItems="center" py={1.5} px={1} gap={1}>

                    {tags.length > 0 ? (
                        tags.map((tag: ClusterChip) => {
                            const deletable = isChipDeletable(tag, region, regionScoped);
                            const regionCode = chipRegionCode(tag, countries);
                            const scopeBadge = regionCode
                                ? <Box component="span" sx={{ fontSize: 10, fontWeight: 600, color: "#919EAB" }}>{regionCode}</Box>
                                : <Iconify icon="mdi:earth" width={12} />;
                            const chip = (
                                <Chip
                                    key={chipKey(tag)}
                                    label={tag.name}
                                    size="small"
                                    icon={deletable ? undefined : scopeBadge}
                                    deleteIcon={
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="12"
                                            height="12"
                                            viewBox="0 0 12 12"
                                            fill="none"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M5.99715 6.70429L8.82429 9.53163C8.91854 9.62267 9.04478 9.67305 9.17582 9.67191C9.30685 9.67077 9.4322 9.61821 9.52486 9.52554C9.61752 9.43287 9.67008 9.30752 9.67122 9.17648C9.67235 9.04543 9.62198 8.91918 9.53095 8.82492L6.70381 5.99758L9.53095 3.17025C9.62198 3.07599 9.67235 2.94974 9.67122 2.81869C9.67008 2.68765 9.61752 2.56229 9.52486 2.46963C9.4322 2.37696 9.30685 2.3244 9.17582 2.32326C9.04478 2.32212 8.91854 2.3725 8.82429 2.46354L5.99715 5.29088L3.17002 2.46354C3.07534 2.37475 2.94983 2.32628 2.82005 2.32839C2.69028 2.33049 2.56641 2.38301 2.47466 2.47483C2.38291 2.56665 2.33049 2.69056 2.32847 2.82035C2.32645 2.95014 2.37501 3.07563 2.46386 3.17025L5.2905 5.99758L2.46336 8.82492C2.41563 8.87102 2.37756 8.92617 2.35136 8.98715C2.32517 9.04813 2.31139 9.11371 2.31081 9.18007C2.31023 9.24644 2.32288 9.31225 2.34801 9.37367C2.37313 9.43509 2.41024 9.4909 2.45717 9.53782C2.50409 9.58475 2.55989 9.62186 2.62131 9.64699C2.68273 9.67212 2.74853 9.68477 2.81489 9.68419C2.88125 9.68361 2.94683 9.66983 3.0078 9.64363C3.06877 9.61744 3.12392 9.57936 3.17002 9.53163L5.99715 6.70429Z"
                                                fill="#919EAB"
                                            />
                                        </svg>
                                    }
                                    variant="soft"
                                    sx={{
                                        borderRadius: "32px",
                                        backgroundColor: "rgba(145, 158, 171, 0.10)",
                                        color: "#5E6D7B",
                                        maxWidth: "150px",

                                        px: "10px",
                                        py: "2px",
                                        pr: "6px",
                                        gap: "6px",
                                        minHeight: "24px",

                                        "& .MuiChip-label": {
                                            px: 0,
                                            py: 0,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            color: "#5E6D7B",
                                        },

                                        "& .MuiChip-label.MuiChip-labelSmall": {
                                            color: "#5E6D7B",
                                        },

                                        "& .MuiChip-deleteIcon": {
                                            width: 12,
                                            height: 12,
                                            color: "#919EAB",
                                            margin: 0,
                                        },

                                        "& .MuiChip-deleteIcon:hover": {
                                            color: "#919EAB",
                                        },
                                    }}
                                    onDelete={deletable ? () => {
                                        untagSearchTerm({
                                            workspaceId: workspace?.id as number,
                                            searchTerm: row.id,
                                            tag: tag.name,
                                            module,
                                            region,
                                        })
                                            .unwrap()
                                            .then(() => {
                                                enqueueSnackbar("Search term unclustered successfully", {
                                                    variant: "success",
                                                });
                                            })
                                            .catch(() => {
                                                enqueueSnackbar("Unclustering search term failed", {
                                                    variant: "error",
                                                });
                                            });
                                    } : undefined}
                                />
                            );
                            if (deletable) return chip;
                            return (
                                <CustomTooltip
                                    key={chipKey(tag)}
                                    arrow
                                    placement="top"
                                    title={regionCode
                                        ? `Assigned to ${tag.region_name} only: remove it from the ${regionCode} tab`
                                        : "Assigned to all regions: remove it from the All tab"}
                                >
                                    <span>{chip}</span>
                                </CustomTooltip>
                            );
                        })
                    ) : (
                        <Typography sx={{ color: "rgba(94, 109, 123, 0.7)" }}>--</Typography>
                    )}
                </Stack>
            </Box>

            <Button
                onClick={addTagsModal.onTrue}
                sx={{
                    borderRadius: 0,
                    m: 0,
                    p: 0,
                    color: "#2F3843",
                    backgroundColor: "rgba(240, 240, 240, 0.70)",
                    minWidth: 'fit-content',
                    "& .MuiButton-startIcon": {
                        ml: 0.5,
                        mr: 0.5,
                    },
                    "&:hover": {
                        backgroundColor: "rgba(240, 240, 240, 0.8)",
                    },
                }} variant="contained" startIcon={<Iconify sx={{ cursor: 'pointer' }} onClick={addTagsModal.onTrue} icon="mdi-light:plus" />} />
            <Dialog fullWidth maxWidth="sm" open={addTagsModal.value} onClose={addTagsModal.onFalse}>
                <FormProvider methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
                    <DialogTitle color={"rgba(51, 60, 71, 1)"}>Add clusters</DialogTitle>
                    <DialogContent sx={{ p: 2 }}>
                        <TextField size="medium" label="Search term" disabled value={row.id} fullWidth sx={{ mt: 2 }} />
                        <GenericInfiniteScrollList name="tags"
                            sx={{ mt: 2 }}
                            size="medium"
                            searchPlaceholder="Type and press enter to add a cluster"
                            fullWidth={true}
                            multiple={true}
                            freeSolo={true}
                            options={tagsOptions}
                            items={deduplicateTagsOptions}
                            transformValue={(valueId: string) => tagsOptions.find((tag: any) => tag.id === valueId)?.label || valueId as any}
                            customItemRenderer={(option) => (
                                <Chip
                                    label={option.label || option as any}
                                    size="small"
                                    variant="soft"
                                    sx={{
                                        backgroundColor: "rgba(145, 158, 171, 0.1)",
                                        color: "rgba(94, 109, 123, 1)"
                                    }}
                                />
                            )}
                            customTagRenderer={(option) => (
                                <Chip
                                    label={option.label || option as any}
                                    size="small"
                                    variant="soft"
                                    sx={{
                                        cursor: "pointer",
                                        marginRight: 0.5,
                                        backgroundColor: "rgba(145, 158, 171, 0.1)",
                                        color: "rgba(94, 109, 123, 1)"
                                    }}
                                    onDelete={() => {
                                        methods.setValue("tags", methods.getValues("tags").filter((tag: any) => tag.id !== option.id));
                                    }}
                                />
                            )}
                            setItems={(items) => setTagsOptions(items)}
                            getOptionLabel={(option) => (option as any).label || option as any}
                            getOptionKey={(option) => (option as any).id || option as any}
                            isOptionEqualToValue={(option, value) => {
                                const isOptionIDEqual = (option.id || option as any) === value;
                                const isOptionLabelEqual = (option.label || option as any) === value;
                                return isOptionIDEqual || isOptionLabelEqual;
                            }}
                        />
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: 'flex-start' }}>
                        <Button sx={{ backgroundColor: "#181818" }} variant="contained" color="primary" type="submit">Add</Button>
                    </DialogActions>
                </FormProvider>
            </Dialog>
        </Stack>
    );
}
